import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loadPlan } from '../../store/authSlice';
import Subscription from './Subscription';
import RequestDialog, { RequestType } from './RequestDialog';

export const SUBSCRIPTION_PATH = '/subscription';
// fired by the axios interceptor when the API reports an ended subscription
export const SUBSCRIPTION_ENDED_EVENT = 'wareozo:subscription-ended';

export interface IPlanStatus {
  enforced: boolean;
  blocked?: boolean;
  status?: string;
  endsAt?: string;
  graceEndsAt?: string;
  paidPlanName?: string;
  pos?: { planCode?: string; status?: string; endsAt?: string } | null;
}

const formatDate = (date?: string) =>
  date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

/**
 * Checks the account's subscription on every page change. When a paid plan has
 * ended, every page is redirected to the Subscription page until it is renewed.
 */
export const useSubscriptionGate = (isLoggedIn: boolean, accountId?: string) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const plan: IPlanStatus | undefined = useAppSelector((state) => state.auth.plan) || undefined;

  // full load (with usage for limit locks) on sign-in, account switch and when the API reports a plan block
  useEffect(() => {
    if (!isLoggedIn || !accountId) return undefined;
    dispatch(loadPlan(true));
    const refresh = () => dispatch(loadPlan(true));
    window.addEventListener(SUBSCRIPTION_ENDED_EVENT, refresh);
    return () => window.removeEventListener(SUBSCRIPTION_ENDED_EVENT, refresh);
  }, [isLoggedIn, accountId, dispatch]);

  // status-only check on every page change
  useEffect(() => {
    if (isLoggedIn && accountId) dispatch(loadPlan(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (plan?.blocked && location.pathname !== SUBSCRIPTION_PATH) navigate(SUBSCRIPTION_PATH, { replace: true });
  }, [plan?.blocked, location.pathname, navigate]);

  return plan;
};

/** Shown instead of every page while the subscription has ended. */
// the ended message itself is the banner above the page (GraceBanner)
export const SubscriptionEnded: FC<{ plan: IPlanStatus }> = () => (
  <Box sx={{ p: 2 }}>
    <Subscription />
  </Box>
);

/** Shown when the page's module is not part of the plan. */
export const PlanLocked: FC = () => {
  const navigate = useNavigate();
  const [request, setRequest] = useState(false);
  return (
    <Box sx={{ p: 2 }}>
      <Alert
        severity='info'
        action={
          <>
            <Button size='small' onClick={() => navigate(SUBSCRIPTION_PATH)} sx={{ textTransform: 'none' }}>View plans</Button>
            <Button size='small' onClick={() => setRequest(true)} sx={{ textTransform: 'none' }}>Request upgrade</Button>
          </>
        }
      >
        <b>This feature is not included in your plan.</b> Upgrade your plan to use it.
      </Alert>
      {request && <RequestDialog type='upgrade' onClose={() => setRequest(false)} />}
    </Box>
  );
};

const DISMISSED_KEY = 'subscriptionBannersDismissed';

// closed banners stay closed for this browser session; storage can be blocked, so never fail on it
const readDismissed = (): string[] => {
  try { return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]'); } catch { return []; }
};

/** Renewal banners across the app (ERP and POS, grace and ended), each closable. */
export const GraceBanner: FC<{ plan?: IPlanStatus }> = ({ plan }) => {
  const pos = plan?.pos;
  const [request, setRequest] = useState<RequestType | null>(null);
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);

  const dismiss = (key: string) => {
    const next = [...dismissed, key];
    setDismissed(next);
    try { sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const banners = [
    plan?.status === 'grace' && {
      key: `erp-grace-${plan.endsAt}`, severity: 'warning' as const, type: 'renew' as RequestType,
      text: `Your subscription ended on ${formatDate(plan.endsAt)}. Renew before ${formatDate(plan.graceEndsAt)} to keep using Wareozo without interruption.`,
    },
    plan?.status === 'expired' && {
      key: `erp-expired-${plan.endsAt}`, severity: 'error' as const, type: 'renew' as RequestType,
      text: `Your Wareozo subscription${plan.paidPlanName ? ` (${plan.paidPlanName})` : ''} has ended${plan.endsAt ? ` on ${formatDate(plan.endsAt)}` : ''}. Renew to continue using Wareozo; your data is safe.`,
    },
    pos?.status === 'grace' && {
      key: `pos-grace-${pos.endsAt}`, severity: 'warning' as const, type: 'pos_renew' as RequestType,
      text: `Your POS subscription ended on ${formatDate(pos.endsAt)}. Renew soon so POS billing is not interrupted.`,
    },
    pos?.status === 'expired' && {
      key: `pos-expired-${pos.endsAt}`, severity: 'error' as const, type: 'pos_renew' as RequestType,
      text: `Your POS subscription has ended${pos.endsAt ? ` on ${formatDate(pos.endsAt)}` : ''} and POS is switched off. Contact the Wareozo team to renew POS.`,
    },
  ].filter((b): b is { key: string; severity: 'warning' | 'error'; type: RequestType; text: string } => !!b && !dismissed.includes(b.key));

  return (
    <>
      {request && <RequestDialog type={request} onClose={() => setRequest(null)} />}
      {banners.map((b) => (
        <Alert
          key={b.key}
          severity={b.severity}
          sx={{ borderRadius: 0, alignItems: 'center' }}
          action={
            <>
              <Button size='small' color='inherit' onClick={() => setRequest(b.type)} sx={{ textTransform: 'none', fontWeight: 600 }}>Request renewal</Button>
              <IconButton size='small' color='inherit' aria-label='Close' onClick={() => dismiss(b.key)}><CloseIcon fontSize='small' /></IconButton>
            </>
          }
        >
          {b.text}
        </Alert>
      ))}
    </>
  );
};

/** /subscription page for accounts that are not blocked. */
export const SubscriptionPage: FC = () => (
  <Box sx={{ p: 2 }}>
    <Subscription />
  </Box>
);
